import { defineMessages } from "../define";

/**
 * Developer monitoring board: page shell, overview, health findings and the words every tab shares.
 * The tabs have their own areas (monitoring-pods, monitoring-flow, monitoring-traffic).
 */
export default defineMessages({
  en: {
    "monitoring.title": "Monitoring",
    "monitoring.description":
      "Live state of the whole pizza checker: GPU pods, cameras, the pizza flow, requests and errors.",
    "monitoring.forbidden": "The monitoring board is available to the developer role only.",
    "monitoring.loadFailed": "Could not load: {reason}",
    "monitoring.retry": "Retry",
    "monitoring.noData": "No data in this window",
    "monitoring.chart.legend": "Series",
    "monitoring.updated": "Updated {ago}",
    "monitoring.updating": "Updating…",
    "monitoring.notUpdated": "Not updated yet",

    // tabs
    "monitoring.tab.overview": "Overview",
    "monitoring.tab.pods": "GPU pods",
    "monitoring.tab.cameras": "Cameras",
    "monitoring.tab.pipeline": "Pizza flow",
    "monitoring.tab.requests": "Requests",
    "monitoring.tab.events": "Errors & events",
    "monitoring.tab.backend": "Backend",

    // header controls
    "monitoring.window": "Window",
    "monitoring.window.1h": "1 h",
    "monitoring.window.6h": "6 h",
    "monitoring.window.24h": "24 h",
    "monitoring.window.7d": "7 days",
    "monitoring.window.long.1h": "last hour",
    "monitoring.window.long.6h": "last 6 hours",
    "monitoring.window.long.24h": "last 24 hours",
    "monitoring.window.long.7d": "last 7 days",
    "monitoring.autoRefresh": "Auto-refresh",
    "monitoring.pause": "Pause updates",
    "monitoring.resume": "Resume updates",
    "monitoring.paused": "Paused",
    "monitoring.refreshNow": "Refresh now",
    "monitoring.scope": "Data source",
    "monitoring.scope.role": "This environment ({role})",
    "monitoring.scope.all": "All environments",
    "monitoring.scope.instance": "Instance {instance}",
    "monitoring.scope.current": "this instance",
    "monitoring.scope.hint":
      "Metrics and events are written by every backend process. By default the board shows the processes of the environment it is talking to, so history continues across deploys.",

    // overall status
    "monitoring.status.ok": "Everything is running",
    "monitoring.status.warning": "Something looks off",
    "monitoring.status.critical": "Problem needs attention",
    "monitoring.status.okDetail": "No problem found in pods, cameras, the pizza flow, requests or the backend.",
    "monitoring.status.findings.one": "{count} finding",
    "monitoring.status.findings.few": "{count} findings",
    "monitoring.status.findings.other": "{count} findings",
    "monitoring.status.checkedAt": "Checked {ago}",
    "monitoring.status.sectionError": "Part of the checks could not run ({section}): {error}",

    // severities, levels, areas
    "monitoring.severity.critical": "Critical",
    "monitoring.severity.warning": "Warning",
    "monitoring.severity.info": "Info",
    "monitoring.level.error": "Error",
    "monitoring.level.warning": "Warning",
    "monitoring.level.info": "Info",
    "monitoring.area.pods": "GPU pods",
    "monitoring.area.pipeline": "Pizza flow",
    "monitoring.area.cameras": "Cameras",
    "monitoring.area.http": "Requests",
    "monitoring.area.backend": "Backend",
    "monitoring.area.integrations": "Integrations",
    "monitoring.finding.since": "since {time}",
    "monitoring.finding.open": "Open",

    // findings (code -> sentence)
    "monitoring.finding.pods_empty": "No GPU pod in the pool: every evaluation fails until the autoscaler adds one",
    "monitoring.finding.pods_scaled_to_zero": "No GPU pod is running (autoscaler scaled to zero); fine while no pizzas are coming",
    "monitoring.finding.pods_all_down": "All {total} GPU pods are down: evaluations cannot run",
    "monitoring.finding.pod_down": "GPU pod {endpoint} is unhealthy ({error})",
    "monitoring.finding.pod_pool_errors": "Inference requests that failed on every pod (15 min): {count}",
    "monitoring.finding.pod_errors": "GPU pod {endpoint}: failed requests in 15 min: {count} ({rate})",
    "monitoring.finding.pod_slow": "GPU inference is slow: p95 {p95_ms} over {n} requests (15 min)",
    "monitoring.finding.pod_queue": "Inference requests are queueing (up to {max} waiting)",
    "monitoring.finding.pods_config_mismatch":
      "The pod pool differs from the autoscaler snapshot (configured {configured}, running {live})",
    "monitoring.finding.pod_preload_failed":
      "GPU pod {endpoint}: the models failed to load, the pod never becomes ready ({error})",
    "monitoring.finding.pod_model_on_cpu": "GPU pod {endpoint}: {models} run on the CPU instead of the GPU (10–50× slower)",
    "monitoring.finding.pod_gpu_hot": "GPU pod {endpoint}: the GPU reached {temperature} °C in 5 min",
    "monitoring.finding.pod_gpu_throttled": "GPU pod {endpoint}: GPU clocks throttled ({reasons})",
    "monitoring.finding.pod_gpu_memory_high": "GPU pod {endpoint}: GPU memory nearly full ({used_mb} of {total_mb})",
    "monitoring.finding.pod_loop_blocked": "GPU pod {endpoint}: the pod's event loop was blocked up to {max_ms} in 5 min",
    "monitoring.finding.pod_server_errors": "GPU pod {endpoint}: server errors on the pod in 5 min: {count} (last: {error})",
    "monitoring.finding.pod_image_mismatch": "GPU pods run different images ({images})",
    "monitoring.finding.pizzas_stuck_evaluation": "Pizzas with a photo but no verdict (evaluation failed or hangs): {count}",
    "monitoring.finding.pizzas_photo_missing": "Captured pizzas whose photo never arrived from the camera: {count}",
    "monitoring.finding.evaluation_failures": "Failed evaluations in the last 15 min: {count}",
    "monitoring.finding.verdict_slow": "The verdict takes long: p95 {p95_ms} from capture to verdict",
    "monitoring.finding.upload_slow": "Photos arrive slowly: p95 {p95_ms} from trigger to upload",
    "monitoring.finding.invalid_photo_spike":
      "{branch}: {share} of the last {n} photos are invalid or without a pizza (camera moved or dirty?)",
    "monitoring.finding.invalid_photo_high":
      "{branch}: {share} of {n} photos in 24 h are invalid or without a pizza (a lasting high share)",
    "monitoring.finding.capture_missing": "{branch}: packed pizzas never photographed (the camera was not triggered): {count}",
    "monitoring.finding.capture_missing_manual": "{branch}: packed pizzas without a photo (staff trigger the capture there): {count}",
    "monitoring.finding.orders_stopped":
      "No order in the last {minutes} min, usually about {expected} (BiteBerry webhooks?)",
    "monitoring.finding.capture_trigger_failures": "Camera captures that could not be triggered (15 min): {count}",
    "monitoring.finding.upload_rejections": "Camera uploads refused in 15 min: {count}",
    "monitoring.finding.thingsboard_unreachable": "ThingsBoard is unreachable: {error}",
    "monitoring.finding.camera_offline": "Camera {camera} at {branch} is offline",
    "monitoring.finding.camera_restart_pending":
      "Camera {camera}: a restart is requested but the camera has not picked it up",
    "monitoring.finding.branch_without_camera": "{branch} captures photos but has no camera assigned",
    "monitoring.finding.http_errors": "Server errors (5xx) in 15 min: {count} ({rate} of requests)",
    "monitoring.finding.http_slow": "Requests are slow: p95 {p95_ms} (15 min)",
    "monitoring.finding.loop_blocked": "The backend event loop was blocked for {max_ms}",
    "monitoring.finding.loop_lag": "The backend event loop lags (up to {max_ms} in 5 min)",
    "monitoring.finding.recent_restart": "The backend restarted {uptime_s} ago",
    "monitoring.finding.monitoring_persist_failed": "Monitoring data cannot be saved: {error}",
    "monitoring.finding.s3_backlog": "Photo layers waiting for the upload to S3: {max}",
    "monitoring.finding.asset_writes_failed": "Photo layers that failed to upload to S3 (15 min): {count}",
    "monitoring.finding.dependency_down": "{name} is unreachable: {error}",
    "monitoring.finding.dependency_slow": "{name} answers slowly ({ms})",

    // KPI tiles
    "monitoring.kpi.pods": "GPU pods",
    "monitoring.kpi.podsValue": "{healthy} / {total} healthy",
    "monitoring.kpi.podsNone": "No pod running",
    "monitoring.kpi.podsDetail": "requests {requests} · failed {failures} · p95 {p95} (15 min)",
    "monitoring.kpi.cameras": "Cameras",
    "monitoring.kpi.camerasValue": "{online} / {watched} online",
    "monitoring.kpi.camerasDetail": "{offline} offline, {unknown} unknown · {total} in ThingsBoard",
    "monitoring.kpi.captures": "Captures (1 h)",
    "monitoring.kpi.capturesDetail": "usually {usual} · orders {orders} (usually {ordersUsual})",
    "monitoring.kpi.capturesDetailNoBaseline": "orders {orders}",
    "monitoring.kpi.lastCapture": "last {ago}",
    "monitoring.kpi.verdict": "Capture → verdict",
    "monitoring.kpi.verdictDetail": "p50 {p50} · verdicts {n} (1 h)",
    "monitoring.kpi.stuck": "Stuck pizzas",
    "monitoring.kpi.stuckDetail": "without verdict {stuck} (1 h) · never captured {uncaptured}",
    "monitoring.kpi.requests": "Requests (15 min)",
    "monitoring.kpi.requestsDetail": "{errors} × 5xx · {clientErrors} × 4xx · p95 {p95}",
    "monitoring.kpi.errors": "Errors (1 h)",
    "monitoring.kpi.errorsDetail": "warnings {warnings} · errors in 15 min {recent}",
    "monitoring.kpi.backend": "Backend",
    "monitoring.kpi.backendValue": "up {uptime}",
    "monitoring.kpi.backendDetail": "loop lag {lag} · {rss} · CPU {cpu}",

    // overview
    "monitoring.overview.flow": "Pizza flow",
    "monitoring.overview.flowDescription": "Captured pizzas by verdict, orders and the usual volume ({window})",
    "monitoring.overview.latency": "Capture → verdict",
    "monitoring.overview.latencyDescription": "How long a pizza waits for its verdict ({window})",
    "monitoring.overview.pods": "GPU inference",
    "monitoring.overview.podsDescription": "Latency of the GPU pods and failed attempts ({window})",
    "monitoring.overview.errors": "Errors and warnings",
    "monitoring.overview.errorsDescription": "Recorded events per level ({window})",
    "monitoring.overview.recentCaptures": "Latest captures",
    "monitoring.overview.recentCapturesDescription": "The last pizzas through the pipeline",
    "monitoring.overview.recentEvents": "Latest events",
    "monitoring.overview.recentEventsDescription": "Errors, warnings and state changes of this instance",
    "monitoring.overview.noEvents": "No event recorded since the backend started.",
    "monitoring.overview.noCaptures": "No capture in this window.",
    "monitoring.overview.showAll": "Show all",

    // shared series / column names
    "monitoring.series.orders": "Orders",
    "monitoring.series.captured": "Captured",
    "monitoring.series.usualCaptured": "Usual captures",
    "monitoring.series.usualOrders": "Usual orders",
    "monitoring.series.p50": "p50",
    "monitoring.series.p95": "p95",
    "monitoring.series.p99": "p99",
    "monitoring.series.failures": "Failed attempts",
    "monitoring.verdict.ok": "OK",
    "monitoring.verdict.nok": "NOK",
    "monitoring.verdict.invalid_photo": "Invalid photo",
    "monitoring.verdict.no_pizza": "No pizza",
    "monitoring.verdict.photo": "Photo problem",
    "monitoring.verdict.pending": "Evaluating",
    "monitoring.verdict.missing": "Stuck",
    "monitoring.col.time": "Time",
    "monitoring.col.pizza": "Pizza",
    "monitoring.col.branch": "Branch",
    "monitoring.col.verdict": "Verdict",
    "monitoring.col.latency": "Latency",
    "monitoring.col.message": "Message",
    "monitoring.unknownBranch": "Unknown branch",

    // time
    "monitoring.time.justNow": "just now",
    "monitoring.time.ago": "{duration} ago",
  },
  cs: {
    "monitoring.title": "Monitoring",
    "monitoring.description":
      "Živý stav celého pizza checkeru: GPU pody, kamery, tok pizz, požadavky a chyby.",
    "monitoring.forbidden": "Monitoring je dostupný jen pro roli vývojáře.",
    "monitoring.loadFailed": "Nepodařilo se načíst: {reason}",
    "monitoring.retry": "Zkusit znovu",
    "monitoring.noData": "V tomto období nejsou žádná data",
    "monitoring.chart.legend": "Řady",
    "monitoring.updated": "Aktualizováno {ago}",
    "monitoring.updating": "Aktualizuji…",
    "monitoring.notUpdated": "Zatím neaktualizováno",

    "monitoring.tab.overview": "Přehled",
    "monitoring.tab.pods": "GPU pody",
    "monitoring.tab.cameras": "Kamery",
    "monitoring.tab.pipeline": "Tok pizz",
    "monitoring.tab.requests": "Požadavky",
    "monitoring.tab.events": "Chyby a události",
    "monitoring.tab.backend": "Backend",

    "monitoring.window": "Období",
    "monitoring.window.1h": "1 h",
    "monitoring.window.6h": "6 h",
    "monitoring.window.24h": "24 h",
    "monitoring.window.7d": "7 dní",
    "monitoring.window.long.1h": "poslední hodina",
    "monitoring.window.long.6h": "posledních 6 hodin",
    "monitoring.window.long.24h": "posledních 24 hodin",
    "monitoring.window.long.7d": "posledních 7 dní",
    "monitoring.autoRefresh": "Automatická obnova",
    "monitoring.pause": "Pozastavit aktualizace",
    "monitoring.resume": "Obnovit aktualizace",
    "monitoring.paused": "Pozastaveno",
    "monitoring.refreshNow": "Obnovit teď",
    "monitoring.scope": "Zdroj dat",
    "monitoring.scope.role": "Toto prostředí ({role})",
    "monitoring.scope.all": "Všechna prostředí",
    "monitoring.scope.instance": "Instance {instance}",
    "monitoring.scope.current": "tato instance",
    "monitoring.scope.hint":
      "Metriky a události zapisuje každý proces backendu. Výchozí pohled ukazuje procesy prostředí, se kterým admin mluví, takže historie pokračuje i přes nasazení.",

    "monitoring.status.ok": "Všechno běží",
    "monitoring.status.warning": "Něco nevypadá dobře",
    "monitoring.status.critical": "Problém vyžaduje pozornost",
    "monitoring.status.okDetail": "V podech, kamerách, toku pizz, požadavcích ani backendu není žádný problém.",
    "monitoring.status.findings.one": "{count} nález",
    "monitoring.status.findings.few": "{count} nálezy",
    "monitoring.status.findings.other": "{count} nálezů",
    "monitoring.status.checkedAt": "Zkontrolováno {ago}",
    "monitoring.status.sectionError": "Část kontrol nešlo provést ({section}): {error}",

    "monitoring.severity.critical": "Kritické",
    "monitoring.severity.warning": "Varování",
    "monitoring.severity.info": "Info",
    "monitoring.level.error": "Chyba",
    "monitoring.level.warning": "Varování",
    "monitoring.level.info": "Info",
    "monitoring.area.pods": "GPU pody",
    "monitoring.area.pipeline": "Tok pizz",
    "monitoring.area.cameras": "Kamery",
    "monitoring.area.http": "Požadavky",
    "monitoring.area.backend": "Backend",
    "monitoring.area.integrations": "Integrace",
    "monitoring.finding.since": "od {time}",
    "monitoring.finding.open": "Otevřít",

    "monitoring.finding.pods_empty": "V poolu není žádný GPU pod: každé vyhodnocení selže, dokud autoscaler nějaký nepřidá",
    "monitoring.finding.pods_scaled_to_zero": "Neběží žádný GPU pod (autoscaler je škáloval na nulu); v pořádku, dokud nechodí pizzy",
    "monitoring.finding.invalid_photo_high":
      "{branch}: {share} z {n} fotek za 24 h je neplatných nebo bez pizzy (dlouhodobě vysoký podíl)",
    "monitoring.finding.pods_all_down": "Všech {total} GPU podů je mimo provoz: vyhodnocení nemůže běžet",
    "monitoring.finding.pod_down": "GPU pod {endpoint} není v pořádku ({error})",
    "monitoring.finding.pod_pool_errors": "Požadavky na inferenci, které selhaly na všech podech (15 min): {count}",
    "monitoring.finding.pod_errors": "GPU pod {endpoint}: neúspěšné požadavky za 15 min: {count} ({rate})",
    "monitoring.finding.pod_slow": "GPU inference je pomalá: p95 {p95_ms} z {n} požadavků (15 min)",
    "monitoring.finding.pod_queue": "Požadavky na inferenci čekají ve frontě (až {max} najednou)",
    "monitoring.finding.pods_config_mismatch":
      "Pool podů se liší od snapshotu autoscaleru (nastaveno {configured}, běží {live})",
    "monitoring.finding.pod_preload_failed":
      "GPU pod {endpoint}: modely se nepodařilo načíst, pod nebude nikdy připraven ({error})",
    "monitoring.finding.pod_model_on_cpu": "GPU pod {endpoint}: {models} běží na CPU místo na GPU (10–50× pomaleji)",
    "monitoring.finding.pod_gpu_hot": "GPU pod {endpoint}: GPU se za 5 min zahřálo na {temperature} °C",
    "monitoring.finding.pod_gpu_throttled": "GPU pod {endpoint}: takty GPU jsou omezené ({reasons})",
    "monitoring.finding.pod_gpu_memory_high": "GPU pod {endpoint}: paměť GPU je téměř plná ({used_mb} z {total_mb})",
    "monitoring.finding.pod_loop_blocked": "GPU pod {endpoint}: event loop podu byl za 5 min zablokovaný až {max_ms}",
    "monitoring.finding.pod_server_errors": "GPU pod {endpoint}: chyby serveru na podu za 5 min: {count} (poslední: {error})",
    "monitoring.finding.pod_image_mismatch": "GPU pody mají různé image ({images})",
    "monitoring.finding.pizzas_stuck_evaluation": "Pizzy s fotkou, ale bez verdiktu (vyhodnocení selhalo nebo visí): {count}",
    "monitoring.finding.pizzas_photo_missing": "Vyfocené pizzy, ke kterým z kamery nikdy nedorazila fotka: {count}",
    "monitoring.finding.evaluation_failures": "Neúspěšná vyhodnocení za posledních 15 min: {count}",
    "monitoring.finding.verdict_slow": "Verdikt trvá dlouho: p95 {p95_ms} od vyfocení po verdikt",
    "monitoring.finding.upload_slow": "Fotky chodí pomalu: p95 {p95_ms} od spuštění po upload",
    "monitoring.finding.invalid_photo_spike":
      "{branch}: {share} z posledních {n} fotek je neplatných nebo bez pizzy (posunutá nebo špinavá kamera?)",
    "monitoring.finding.capture_missing": "{branch}: zabalené pizzy, které nebyly vyfoceny (kamera se nespustila): {count}",
    "monitoring.finding.capture_missing_manual": "{branch}: zabalené pizzy bez fotky (focení tam spouští obsluha): {count}",
    "monitoring.finding.orders_stopped":
      "Za posledních {minutes} min nepřišla žádná objednávka, obvykle jich je asi {expected} (webhooky BiteBerry?)",
    "monitoring.finding.capture_trigger_failures": "Snímky, které se na kameře nepodařilo spustit (15 min): {count}",
    "monitoring.finding.upload_rejections": "Odmítnuté uploady z kamer za 15 min: {count}",
    "monitoring.finding.thingsboard_unreachable": "ThingsBoard je nedostupný: {error}",
    "monitoring.finding.camera_offline": "Kamera {camera} na pobočce {branch} je offline",
    "monitoring.finding.camera_restart_pending":
      "Kamera {camera}: restart je vyžádán, ale kamera si ho zatím nepřevzala",
    "monitoring.finding.branch_without_camera": "{branch} fotí pizzy, ale nemá přiřazenou kameru",
    "monitoring.finding.http_errors": "Chyby serveru (5xx) za 15 min: {count} ({rate} požadavků)",
    "monitoring.finding.http_slow": "Požadavky jsou pomalé: p95 {p95_ms} (15 min)",
    "monitoring.finding.loop_blocked": "Event loop backendu byl zablokovaný {max_ms}",
    "monitoring.finding.loop_lag": "Event loop backendu se zpožďuje (až {max_ms} za 5 min)",
    "monitoring.finding.recent_restart": "Backend se restartoval před {uptime_s}",
    "monitoring.finding.monitoring_persist_failed": "Data monitoringu se nedaří ukládat: {error}",
    "monitoring.finding.s3_backlog": "Vrstvy fotek čekající na nahrání do S3: {max}",
    "monitoring.finding.asset_writes_failed": "Vrstvy fotek, které se nepodařilo nahrát do S3 (15 min): {count}",
    "monitoring.finding.dependency_down": "{name} je nedostupné: {error}",
    "monitoring.finding.dependency_slow": "{name} odpovídá pomalu ({ms})",

    "monitoring.kpi.pods": "GPU pody",
    "monitoring.kpi.podsValue": "{healthy} / {total} v pořádku",
    "monitoring.kpi.podsNone": "Žádný pod neběží",
    "monitoring.kpi.podsDetail": "požadavky {requests} · neúspěšné {failures} · p95 {p95} (15 min)",
    "monitoring.kpi.cameras": "Kamery",
    "monitoring.kpi.camerasValue": "{online} / {watched} online",
    "monitoring.kpi.camerasDetail": "offline {offline} · neznámý stav {unknown} · v ThingsBoardu {total}",
    "monitoring.kpi.captures": "Snímky (1 h)",
    "monitoring.kpi.capturesDetail": "obvykle {usual} · objednávky {orders} (obvykle {ordersUsual})",
    "monitoring.kpi.capturesDetailNoBaseline": "objednávky {orders}",
    "monitoring.kpi.lastCapture": "poslední {ago}",
    "monitoring.kpi.verdict": "Vyfocení → verdikt",
    "monitoring.kpi.verdictDetail": "p50 {p50} · verdikty {n} (1 h)",
    "monitoring.kpi.stuck": "Zaseklé pizzy",
    "monitoring.kpi.stuckDetail": "bez verdiktu {stuck} (1 h) · nikdy nevyfoceno {uncaptured}",
    "monitoring.kpi.requests": "Požadavky (15 min)",
    "monitoring.kpi.requestsDetail": "{errors} × 5xx · {clientErrors} × 4xx · p95 {p95}",
    "monitoring.kpi.errors": "Chyby (1 h)",
    "monitoring.kpi.errorsDetail": "varování {warnings} · chyby za 15 min {recent}",
    "monitoring.kpi.backend": "Backend",
    "monitoring.kpi.backendValue": "běží {uptime}",
    "monitoring.kpi.backendDetail": "zpoždění loopu {lag} · {rss} · CPU {cpu}",

    "monitoring.overview.flow": "Tok pizz",
    "monitoring.overview.flowDescription": "Vyfocené pizzy podle verdiktu, objednávky a obvyklý objem ({window})",
    "monitoring.overview.latency": "Vyfocení → verdikt",
    "monitoring.overview.latencyDescription": "Jak dlouho pizza čeká na verdikt ({window})",
    "monitoring.overview.pods": "GPU inference",
    "monitoring.overview.podsDescription": "Latence GPU podů a neúspěšné pokusy ({window})",
    "monitoring.overview.errors": "Chyby a varování",
    "monitoring.overview.errorsDescription": "Zaznamenané události podle úrovně ({window})",
    "monitoring.overview.recentCaptures": "Poslední snímky",
    "monitoring.overview.recentCapturesDescription": "Poslední pizzy, které prošly pipeline",
    "monitoring.overview.recentEvents": "Poslední události",
    "monitoring.overview.recentEventsDescription": "Chyby, varování a změny stavu této instance",
    "monitoring.overview.noEvents": "Od startu backendu nebyla zaznamenána žádná událost.",
    "monitoring.overview.noCaptures": "V tomto období není žádný snímek.",
    "monitoring.overview.showAll": "Zobrazit vše",

    "monitoring.series.orders": "Objednávky",
    "monitoring.series.captured": "Vyfoceno",
    "monitoring.series.usualCaptured": "Obvyklé snímky",
    "monitoring.series.usualOrders": "Obvyklé objednávky",
    "monitoring.series.p50": "p50",
    "monitoring.series.p95": "p95",
    "monitoring.series.p99": "p99",
    "monitoring.series.failures": "Neúspěšné pokusy",
    "monitoring.verdict.ok": "OK",
    "monitoring.verdict.nok": "NEOK",
    "monitoring.verdict.invalid_photo": "Neplatná fotka",
    "monitoring.verdict.no_pizza": "Bez pizzy",
    "monitoring.verdict.photo": "Problém s fotkou",
    "monitoring.verdict.pending": "Vyhodnocuje se",
    "monitoring.verdict.missing": "Zaseklá",
    "monitoring.col.time": "Čas",
    "monitoring.col.pizza": "Pizza",
    "monitoring.col.branch": "Pobočka",
    "monitoring.col.verdict": "Verdikt",
    "monitoring.col.latency": "Latence",
    "monitoring.col.message": "Zpráva",
    "monitoring.unknownBranch": "Neznámá pobočka",

    "monitoring.time.justNow": "právě teď",
    "monitoring.time.ago": "před {duration}",
  },
});
